// Fila de sincronização do app-ponto.
// Pega lotes da Dexie (lib/db.ts) e envia em POST /ponto/sync conforme o
// contrato { registros, nowDevice } → { resultados: [{ id, resultado, ... }] }.
// 'ok' e 'duplicado' saem da fila; 'erro' faz backoff exponencial e, após
// 10 tentativas, o registro vai para a quarentena (regras em lib/db.ts).
// Listener online (capacitor/network), polling 30s, e Background Sync API
// como fallback no service worker.

import { Network } from '@capacitor/network';
import { SYNC_BATCH_SIZE } from '@app-ponto/shared';
import { api } from './api';
import {
  proximosPendentes,
  marcarEnviado,
  marcarErro,
  contarPendentes,
  type PontoPendente,
  type RegistroServidor,
} from './db';

const POLL_MS = 30_000;
const BACKGROUND_SYNC_TAG = 'app-ponto-sync';
const SYNC_EVENT = 'app-ponto:sync';

let rodando = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let netUnsub: (() => Promise<void>) | (() => void) | null = null;
let iniciado = false;

export interface SyncRunResult {
  enviados: number;
  falhas: number;
  pendentesRestantes: number;
}

// Espelho do SyncResponseSchema (contrato 1)
interface SyncBatchResponse {
  resultados?: Array<{
    id: string;
    resultado: 'ok' | 'duplicado' | 'erro';
    motivo?: string;
    registro?: RegistroServidor;
  }>;
}

function montarPayload(itens: PontoPendente[]) {
  return {
    nowDevice: new Date().toISOString(),
    registros: itens.map((r) => ({
      id: r.id,
      tipo: r.tipo,
      timestampDevice: r.timestampDevice,
      ...(r.latitude != null ? { latitude: r.latitude } : {}),
      ...(r.longitude != null ? { longitude: r.longitude } : {}),
      ...(r.accuracyMetros != null ? { accuracyMetros: r.accuracyMetros } : {}),
      ...(r.biometriaScore != null ? { biometriaScore: r.biometriaScore } : {}),
      ...(r.biometriaModelo ? { biometriaModelo: r.biometriaModelo } : {}),
      ...(r.fotoCapturadaBase64
        ? { fotoCapturadaBase64: r.fotoCapturadaBase64 }
        : {}),
      ...(r.justificativa ? { justificativa: r.justificativa } : {}),
    })),
  };
}

function dispatchSyncEvent(detail: SyncRunResult): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail }));
  } catch {
    // ignore
  }
}

async function isOnline(): Promise<boolean> {
  try {
    const s = await Network.getStatus();
    return !!s.connected;
  } catch {
    if (typeof navigator !== 'undefined') return navigator.onLine;
    return true;
  }
}

/**
 * Processa um único lote (até SYNC_BATCH_SIZE). Idempotente; respeita mutex.
 */
export async function sincronizarAgora(): Promise<SyncRunResult> {
  if (rodando) {
    const pendentes = await contarPendentes();
    return { enviados: 0, falhas: 0, pendentesRestantes: pendentes };
  }
  rodando = true;
  let enviados = 0;
  let falhas = 0;
  try {
    if (!(await isOnline())) {
      const pendentes = await contarPendentes();
      const result = { enviados, falhas, pendentesRestantes: pendentes };
      dispatchSyncEvent(result);
      return result;
    }

    const lote = await proximosPendentes(SYNC_BATCH_SIZE);
    if (lote.length === 0) {
      const result = { enviados, falhas, pendentesRestantes: 0 };
      dispatchSyncEvent(result);
      return result;
    }

    try {
      const payload = montarPayload(lote);
      const resp = await api.post<SyncBatchResponse>('/ponto/sync', payload);
      const data = resp.data ?? {};

      const resultadoPorId = new Map<
        string,
        { resultado: string; motivo?: string; registro?: RegistroServidor }
      >();
      for (const r of data.resultados ?? []) {
        resultadoPorId.set(r.id, {
          resultado: String(r.resultado),
          motivo: r.motivo,
          registro: r.registro,
        });
      }

      for (const item of lote) {
        const r = resultadoPorId.get(item.id);
        if (!r) {
          await marcarErro(item.id, 'sem ack do servidor');
          falhas++;
          continue;
        }
        if (r.resultado === 'erro') {
          await marcarErro(item.id, r.motivo ?? 'erro do servidor');
          falhas++;
        } else {
          // 'ok' e 'duplicado' saem da fila — guarda a verdade do servidor
          // (status, dentroGeofence, biometriaVerificadaServidor...) p/ Histórico.
          const status = r.registro?.status ?? (r.resultado === 'duplicado' ? 'duplicado' : 'validado');
          await marcarEnviado(item.id, status, r.registro);
          enviados++;
        }
      }
    } catch (err: unknown) {
      const e = err as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      const msg =
        e.response?.data?.message ?? e.message ?? 'erro de rede';
      for (const item of lote) {
        await marcarErro(item.id, msg);
        falhas++;
      }
    }

    const pendentesRestantes = await contarPendentes();
    const result: SyncRunResult = { enviados, falhas, pendentesRestantes };
    dispatchSyncEvent(result);
    return result;
  } finally {
    rodando = false;
  }
}

/**
 * Inicia o loop de sync: agenda 1ª passada, escuta network online,
 * registra polling 30s, e (se suportado) registra background-sync no SW.
 * Idempotente — chamadas repetidas não duplicam timers/listeners.
 */
export async function iniciarSync(): Promise<() => void> {
  if (iniciado) return pararSync;
  iniciado = true;

  // listener online
  try {
    const handle = await Network.addListener('networkStatusChange', (s) => {
      if (s.connected) void sincronizarAgora();
    });
    netUnsub = handle.remove;
  } catch {
    if (typeof window !== 'undefined') {
      const onOnline = () => void sincronizarAgora();
      window.addEventListener('online', onOnline);
      netUnsub = () => window.removeEventListener('online', onOnline);
    }
  }

  // polling
  pollTimer = setInterval(() => {
    void sincronizarAgora();
  }, POLL_MS);

  // visibility
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void sincronizarAgora();
    });
  }

  // background sync (registra a tag; o handler vive em sw-extra.ts)
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sync = (reg as unknown as {
        sync?: { register: (tag: string) => Promise<void> };
      }).sync;
      if (sync) await sync.register(BACKGROUND_SYNC_TAG);
    } catch {
      // sem suporte; tudo bem
    }
  }

  // primeiro chute
  void sincronizarAgora();

  return pararSync;
}

export function pararSync(): void {
  if (!iniciado) return;
  iniciado = false;
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (netUnsub) {
    try {
      const r = netUnsub();
      if (r && typeof (r as Promise<void>).then === 'function') {
        void (r as Promise<void>).catch(() => undefined);
      }
    } catch {
      // ignore
    }
    netUnsub = null;
  }
}

export const SYNC_EVENT_NAME = SYNC_EVENT;
export const SYNC_TAG = BACKGROUND_SYNC_TAG;
